/**
 * Gate Form Runtime — Main orchestrator
 *
 * Assembles the rule parser, evaluator, form store, components,
 * and styles into a single injectable block for the Gate iframe runtime.
 */

import { serializeRuleParserForRuntime } from "./engine/ruleParser";
import { serializeRuleEvaluatorForRuntime } from "./engine/ruleEvaluator";
import { serializeFormStoreForRuntime } from "./engine/formStore";
import { serializeFormComponentsForRuntime } from "./engine/formComponents";
import { serializeFormStylesForRuntime } from "./engine/formStyles";

/**
 * Generate the complete Form Runtime JavaScript code to embed in the Gate iframe.
 * This is injected as a <script> block after Vue is loaded and before the page mounts.
 */
export const buildFormRuntimeScript = (): string => {
    return [
        "// ── PRM Form Runtime Engine ──",
        serializeRuleParserForRuntime(),
        serializeRuleEvaluatorForRuntime(),
        serializeFormStoreForRuntime(),
        serializeFormComponentsForRuntime(),
        buildFormRegistrationScript(),
    ].join("\n");
};

/**
 * Generate the Form Runtime CSS to embed as a <style> block in the Gate iframe.
 */
export const buildFormRuntimeStyles = (): string => {
    return serializeFormStylesForRuntime();
};

/**
 * Build the script that registers all prm-* components in a Vue app instance.
 * Called by gateRuntimeHtml.ts when creating the Vue app.
 */
const buildFormRegistrationScript = (): string => {
    return `
(function() {
    /**
     * Register all prm-* components on a Vue app instance.
     * Called during Gate runtime bootstrap, before app.mount().
     */
    window.__prmRegisterFormComponents = function(app) {
        var components = window.__prmComponents || {};
        Object.keys(components).forEach(function(name) {
            try {
                app.component(name, components[name]);
            } catch (e) {
                console.error("[prm-form] Failed to register component:", name, e);
            }
        });
    };
})();
`;
};
